// The `members` registry — the single source of truth for WHO may use the app and
// WITH WHAT ROLE. Non-PHI (emails + status + role + metadata only). Keyed by the
// lowercased email. Once firestore.rules ships, only ADMIN_EMAIL may write here and
// each member's `approved`/`role` claim is synced from this doc by a Cloud Function.
import {
  collection, doc, getDocs, setDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { fbDb } from './firebase';

export type Status = 'pending' | 'invited' | 'active' | 'blocked';
export type Role = 'patient' | 'physician' | 'admin';

export interface Member {
  email: string;
  status: Status;            // pending = self-signup awaiting approval; invited = approved, activation code issued
  role: Role;
  displayName?: string;
  org?: string;              // physician's organization
  uid?: string;              // stamped on first sign-in
  invitedBy?: string;
  invitedAt?: unknown;
  activatedAt?: unknown;
  requestedAt?: unknown;     // when the self-signup request was filed
  approvedAt?: unknown;
  note?: string;
}

export const STATUSES: Status[] = ['pending', 'invited', 'active', 'blocked'];
export const ROLES: Role[] = ['patient', 'physician', 'admin'];

const emailKey = (email: string) => email.trim().toLowerCase();
const col = () => collection(fbDb, 'members');

/** Load the whole registry (small; a directory, not PHI). */
export async function listMembers(): Promise<Member[]> {
  const snap = await getDocs(col());
  return snap.docs
    .map((d) => d.data() as Member)
    .sort((a, b) => (a.email || '').localeCompare(b.email || ''));
}

export async function upsertMember(m: Member): Promise<void> {
  const email = emailKey(m.email);
  await setDoc(doc(col(), email), { ...m, email, invitedAt: serverTimestamp() }, { merge: true });
}
export async function setStatus(email: string, status: Status): Promise<void> {
  await updateDoc(doc(col(), emailKey(email)), { status });
}
export async function setRole(email: string, role: Role): Promise<void> {
  await updateDoc(doc(col(), emailKey(email)), { role });
}
export async function removeMember(email: string): Promise<void> {
  await deleteDoc(doc(col(), emailKey(email)));
}

// A representative directory for previewing the console layout before the
// enforcement rules are deployed (nothing is written to Firestore in preview).
export const SAMPLE: Member[] = [
  { email: 'vishnu@dreamgiga.ai', status: 'active', role: 'admin', displayName: 'Vishnu Sundaresan', note: 'Owner' },
  { email: 'eashwer.family@gmail.com', status: 'active', role: 'patient', displayName: 'Eashwer (guardian acct)' },
  { email: 'dr.jsharma@inova.org', status: 'active', role: 'physician', displayName: 'J. Sharma', org: 'Inova Health' },
  { email: 'dr.harik@texaschildrens.org', status: 'active', role: 'physician', displayName: 'A. Harik', org: "Texas Children's" },
  { email: 'newpatient@gmail.com', status: 'invited', role: 'patient', displayName: '—', invitedBy: 'vishnu@dreamgiga.ai' },
  { email: 'wants.access@gmail.com', status: 'pending', role: 'patient', displayName: 'New Requester' },
  { email: 'removed.user@gmail.com', status: 'blocked', role: 'patient', displayName: 'Former user' },
];
